import { useIsAuthenticated } from '@azure/msal-react';
import { AxiosError } from 'axios';
import {
    createContext,
    useState,
    useContext,
    useEffect,
    useCallback
} from 'react';
import {
    Company,
    CurrentUser,
    LoginType,
    Theme,
    UserIdentity,
    UserRoles
} from '@/models/company';
import { UserLoadErrorPage } from './UserLoadErrorPage';
import { BackendError, isBackendError } from '@/utils/errorHandling';
import UsersAPIServiceV2 from '@/services/UserServiceV2';
import { getCompanyIdFromLocalStorage } from '@/services/companyService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/lib/hooks';
import { useQuery } from '@tanstack/react-query';
import { setLoader } from '@/lib/slices/globalLoaderSlice';

/**
 * Insta Admin (platform-level) routes a global admin may use before selecting a company.
 * On these routes we skip the "pick a company" redirect; every other route still requires one.
 */
const PLATFORM_ROUTE_PREFIXES = [
    '/companies/v2',
    '/companyAccounts',
    '/all-employees',
    '/instaconfig',
    '/transaction-monitoring',
    '/house-accounts',
    // Aiia OAuth return leg for a house-account connection — must survive the
    // no-company redirect or the connect flow dies mid-callback.
    '/payments/houseAccountAiiaConnectCallback',
    '/global-reports',
    '/user-communication-history',
    '/campaign-activation-funnel'
];

const isPlatformRoute = (pathname: string) =>
    PLATFORM_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

type UserProviderProps = {
    children: JSX.Element;
    value?: State;
};

type State = {
    user: CurrentUser;
    /**
     * @deprecated The `company` property is deprecated. use useCurrentCompanyDetails react query instead.
     */
    company: Company;
};
const initialState: State = {
    user: {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        role: UserRoles.User
    },
    company: {
        companyId: '',
        createdAt: new Date(),
        currency: 'DKK',
        greetingText: '',
        timeRegistrationProvider: '',
        loginType: LoginType.AzureAD,
        name: '',
        theme: { logoName: '/instapaid_long.png' } as Theme,
        endOfTrial: null,
        requiredConsentsAccepted: true,
        showReports: false,
        allowancePercentage: 0,
        latestWithdrawalDay: undefined,
        withdrawalsLocked: false,
        tipsLive: false,
        employeeWithdrawalFee: 0,
        minimumWithdrawalAmount: 0,
        balanceSummaryTextDa: '',
        balanceSummaryTextEn: '',
        enableInstaSign: false
    }
};

export const UserContext = createContext({
    ...initialState,
    reloadUserData: () => {}
});

export const UserProvider = (props: UserProviderProps) => {
    const isAuthenticated = useIsAuthenticated();

    const [user, setUser] = useState<CurrentUser>();
    const [company, setCompany] = useState<Company>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [errorStatusCode, setErrorStatusCode] = useState<number>();
    const [backendError, setBackendError] = useState<BackendError>();

    const navigation = useNavigate();
    const location = useLocation();

    const handleError = (e: any) => {
        let errorDetails: BackendError | undefined = undefined;
        if (e instanceof AxiosError && isBackendError(e.response?.data)) {
            errorDetails = e.response?.data as BackendError;
            setBackendError(errorDetails);
            setErrorStatusCode(+errorDetails.status);
            setErrorMessage(errorDetails.detail);
        } else {
            const error = 'Could not get user - ' + e.message;
            setErrorStatusCode((e as AxiosError).response?.status ?? undefined);
            setErrorMessage(error);
        }
    };

    const [isCompanySet, setIsCompanySet] = useState<boolean>(false);

    // Company-agnostic identity — available as soon as the user is authenticated,
    // before any company is selected, so the Insta Admin menu can render.
    const { data: identityData } = useQuery({
        queryKey: ['identity', isAuthenticated],
        queryFn: () => UsersAPIServiceV2.getInstance().getIdentity(),
        enabled: isAuthenticated
    });
    const identity = identityData?.data as UserIdentity | undefined;

    const {
        data: userData,
        error: userError,
        isPending: userPending,
        refetch: refetchUser
    } = useQuery({
        queryKey: ['user', isAuthenticated, isCompanySet],
        queryFn: () => UsersAPIServiceV2.getInstance().getCurrentUser(),
        enabled: isAuthenticated && isCompanySet
    });

    useEffect(() => {
        if (userData) {
            setUser(userData.data);
        }
    }, [userData]);

    useEffect(() => {
        if (userError) {
            let e = userError;
            let errorDetails: BackendError | undefined = undefined;
            if (e instanceof AxiosError && isBackendError(e.response?.data)) {
                errorDetails = e.response?.data as BackendError;
                setBackendError(errorDetails);
                setErrorStatusCode(+errorDetails.status);
                setErrorMessage(errorDetails.detail);
            } else {
                const error = 'Could not get user - ' + e.message;
                setErrorStatusCode(
                    (e as AxiosError).response?.status ?? undefined
                );
                setErrorMessage(error);
            }
        }
    }, [userError]);

    const {
        data: companyData,
        error: companyError,
        isPending: companyPending,
        refetch: refetchCompany
    } = useQuery({
        queryKey: ['company', isAuthenticated, isCompanySet],
        queryFn: () => UsersAPIServiceV2.getInstance().getCurrentCompany(),
        enabled: isAuthenticated && isCompanySet
    });

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (companyData) {
            setCompany(companyData.data);
        }
    }, [companyData]);

    useEffect(() => {
        if (companyError) {
            if (getCompanyIdFromLocalStorage()) {
                handleError(companyError);
            }
        }
    }, [companyError]);

    useEffect(() => {
        dispatch(setLoader(companyPending || userPending));
    }, [companyPending, userPending]);

    const loadUserData = useCallback(() => {
        if (!isAuthenticated) {
            return;
        }

        if (!getCompanyIdFromLocalStorage()) {
            // Global admins can use the Insta Admin section without a selected
            // company; only bounce them to the picker on company-scoped routes.
            const stayOnPlatformRoute =
                identity?.isGlobalAdmin && isPlatformRoute(location.pathname);

            if (!stayOnPlatformRoute && location.pathname !== 'companies/v2') {
                navigation('companies/v2');
            }
            return;
        }

        setErrorMessage(undefined);
        setIsCompanySet(true);
    }, [isAuthenticated, identity, location.pathname, navigation]);

    const reloadUserData = useCallback(() => {
        refetchCompany();
        refetchUser();
    }, [refetchCompany, refetchUser]);

    // Before a company is selected the company-scoped user query is disabled, so
    // fall back to the identity so the sidebar knows the caller is a SuperAdmin.
    const effectiveUser: CurrentUser =
        props.value?.user ??
        user ??
        (identity
            ? {
                  ...initialState.user,
                  id: identity.id,
                  email: identity.email,
                  role: identity.role ?? UserRoles.User,
                  isGlobalAdmin: identity.isGlobalAdmin
              }
            : { ...initialState.user });

    useEffect(() => {
        loadUserData();
    }, [loadUserData]);

    return (
        <>
            {errorMessage ? (
                <UserLoadErrorPage
                    backendError={backendError}
                    statusCode={errorStatusCode}
                />
            ) : (
                <>
                    {
                        <UserContext.Provider
                            value={{
                                user: effectiveUser,
                                company: company ?? {
                                    ...initialState.company
                                },
                                reloadUserData: reloadUserData
                            }}
                        >
                            {props.children}
                        </UserContext.Provider>
                    }
                </>
            )}
        </>
    );
};

export const useUser = () => useContext(UserContext);
